import { Mail, Phone, User } from 'lucide-react'
import { Control, FieldErrors, UseFormWatch } from 'react-hook-form'
import { TextInput, SwitchInput } from '@/components/atoms'
import { MultiSelect } from '@/components/ui/MultiSelect'
import { Label } from '@/components/ui/label'
import { UserFormState } from './userFieldsConfig'
import { UserImageSection } from './UserImageSection'

interface UserProfileTabProps {
    mode: 'create' | 'edit' | 'view'
    control: Control<UserFormState>
    watch: UseFormWatch<UserFormState>
    errors: FieldErrors<UserFormState>
    fields: any
    roleOptions: { label: string; value: string }[]
    rolesLoading: boolean
    onSelectImage: () => void
    onRemoveImage: () => void
    onRoleChange: (values: string[]) => void
}

export const UserProfileTab = ({
    mode,
    control,
    watch,
    errors,
    fields,
    roleOptions,
    rolesLoading,
    onSelectImage,
    onRemoveImage,
    onRoleChange,
}: UserProfileTabProps) => {
    const formValues = watch()

    return (
        <div className="space-y-6">
            <UserImageSection
                imageUrl={formValues.image_url_temp}
                mode={mode}
                onSelectClick={onSelectImage}
                onRemoveClick={onRemoveImage}
            />

            <div className="space-y-4">
                <TextInput
                    control={control}
                    name="name"
                    label="Full name"
                    placeholder="John Doe"
                    disabled={mode === 'view'}
                    rules={fields.name.rules}
                    errors={errors}
                    icon={<User className="size-4" />}
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <TextInput
                        control={control}
                        name="email"
                        label="Email"
                        placeholder="john@example.com"
                        disabled={mode === 'view'}
                        rules={fields.email.rules}
                        errors={errors}
                        icon={<Mail className="size-4" />}
                    />

                    <TextInput
                        control={control}
                        name="phone"
                        label="Phone"
                        placeholder="017XXXXXXXX"
                        disabled={mode === 'view'}
                        errors={errors}
                        icon={<Phone className="size-4" />}
                    />
                </div>

                {mode === 'create' && (
                    <TextInput
                        control={control}
                        name="password"
                        label="Password"
                        type="password"
                        placeholder="••••••••"
                        rules={{ required: 'Password is required' }}
                        errors={errors}
                    />
                )}

                <div className="space-y-2">
                    <Label className="text-sm font-normal text-ink">Roles</Label>
                    <MultiSelect
                        options={roleOptions}
                        value={watch('role_ids') || []}
                        onChange={onRoleChange}
                        placeholder={rolesLoading ? 'Loading roles...' : 'Select roles'}
                        disabled={mode === 'view' || rolesLoading}
                    />
                    {errors.role_ids && (
                        <p className="text-sm text-error">
                            {errors.role_ids.message}
                        </p>
                    )}
                    <p className="text-sm text-text-2">
                        Permissions come from these roles unless you override them on the Permissions tab.
                    </p>
                </div>

                <SwitchInput
                    control={control}
                    name="status"
                    label="Account status"
                    description={
                        formValues.status === 'active'
                            ? 'User can access the dashboard.'
                            : 'User access is currently disabled.'
                    }
                    disabled={mode === 'view'}
                    activeValue="active"
                    inactiveValue="inactive"
                />
            </div>
        </div>
    )
}
